'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import HeroSearchSection from './HeroSearchSection';
import FeaturedCategories from './FeaturedCategories';
import TodaysFreshCatch from './TodaysFreshCatch';
import SpecialOffersCarousel from './SpecialOffersCarousel';
import QuickActions from './QuickActions';
import SubscriptionBanner from './SubscriptionBanner';
import TrustSignals from './TrustSignals';

const HomeDashboardInteractive = () => {
  const router = useRouter();
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const categories = [
  {
    id: '1',
    name: 'Fresh Fish',
    image: "https://images.unsplash.com/photo-1706751528557-bbfffabd3efe",
    alt: 'Fresh whole fish displayed on ice with lemon slices and herbs in market setting',
    productCount: 45,
    icon: 'SparklesIcon'
  },
  {
    id: '2',
    name: 'Prawns & Shrimp',
    image: "https://images.unsplash.com/photo-1595728580218-ac0328835376",
    alt: 'Fresh pink prawns and shrimp arranged on white plate with garnish',
    productCount: 28,
    icon: 'FireIcon'
  },
  {
    id: '3',
    name: 'Crabs & Lobster',
    image: "https://images.unsplash.com/photo-1560113723-5a9fc711588a",
    alt: 'Red lobster and crabs on wooden cutting board with seafood tools',
    productCount: 15,
    icon: 'StarIcon'
  },
  {
    id: '4',
    name: 'Dried & Frozen',
    image: "https://images.unsplash.com/photo-1622242170258-ce26bd895765",
    alt: 'Vacuum sealed frozen fish fillets in clear packaging on ice',
    productCount: 32,
    icon: 'CubeIcon'
  }];


  const freshCatch = [
  {
    id: '1',
    name: 'Premium Salmon',
    image: "https://images.unsplash.com/photo-1673767413856-2cfc082c7ea5",
    alt: 'Fresh salmon fillet with pink flesh on white background with herbs',
    price: 899,
    originalPrice: 1099,
    weight: 'kg',
    catchDate: '25 Dec 2025',
    availability: 'in-stock' as const,
    freshnessScore: 98,
    location: 'Arabian Sea'
  },
  {
    id: '2',
    name: 'Tiger Prawns',
    image: "https://images.unsplash.com/photo-1665262734761-065d7c2b1382",
    alt: 'Large tiger prawns with distinctive stripes on ice bed',
    price: 749,
    weight: 'kg',
    catchDate: '25 Dec 2025',
    availability: 'in-stock' as const,
    freshnessScore: 96,
    location: 'Bay of Bengal'
  },
  {
    id: '3',
    name: 'Pomfret Fish',
    image: "https://images.unsplash.com/photo-1673436977947-0787164a9abc",
    alt: 'Silver pomfret fish whole on white plate with lemon wedges',
    price: 649,
    originalPrice: 749,
    weight: 'kg',
    catchDate: '25 Dec 2025',
    availability: 'low-stock' as const,
    freshnessScore: 94,
    location: 'Arabian Sea'
  },
  {
    id: '4',
    name: 'Fresh Tuna',
    image: "https://images.unsplash.com/photo-1691120217303-dfc557ff9c4c",
    alt: 'Tuna steak cuts showing deep red flesh on wooden board',
    price: 1299,
    weight: 'kg',
    catchDate: '25 Dec 2025',
    availability: 'in-stock' as const,
    freshnessScore: 97,
    location: 'Indian Ocean'
  }];


  const offers = [
  {
    id: '1',
    title: 'Weekend Special',
    description: 'Get flat 30% off on all fresh fish and seafood. Limited time offer for this weekend only!',
    image: "https://images.unsplash.com/photo-1703692331076-877c626e4f71",
    alt: 'Variety of fresh fish and seafood displayed on ice with promotional banner',
    discount: '30%',
    validUntil: '29 Dec 2025',
    ctaText: 'Shop Now',
    ctaLink: '/product-listing'
  },
  {
    id: '2',
    title: 'Premium Prawns Deal',
    description: 'Buy 2kg of tiger prawns and get 500g free. Fresh catch delivered to your doorstep.',
    image: "https://images.unsplash.com/photo-1568267641487-2c9405330c96",
    alt: 'Fresh tiger prawns arranged on ice with buy 2 get 1 offer tag',
    discount: 'Buy 2 Get 1',
    validUntil: '31 Dec 2025',
    ctaText: 'Grab Deal',
    ctaLink: '/product-listing'
  },
  {
    id: '3',
    title: 'First Order Bonus',
    description: 'New customers get ₹200 off on orders above ₹1000. Start your fresh fish journey today!',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bac86da7-1764645890950.png",
    alt: 'Welcome offer banner with fresh seafood platter and discount coupon',
    discount: '₹200',
    validUntil: '05 Jan 2026',
    ctaText: 'Claim Offer',
    ctaLink: '/register'
  }];


  const quickActions = [
  {
    id: '1',
    title: 'Repeat Last Order',
    description: 'Quickly reorder your previous purchase with one tap',
    icon: 'ArrowPathIcon',
    link: '/order-tracking',
    color: 'primary'
  },
  {
    id: '2',
    title: 'Price Alerts',
    description: 'Get notified when your favorite fish prices drop',
    icon: 'BellAlertIcon',
    link: '/fish-price-discovery',
    color: 'secondary'
  },
  {
    id: '3',
    title: 'Family Meal Plan',
    description: 'Plan weekly meals based on your budget and preferences',
    icon: 'CalendarDaysIcon',
    link: '/product-listing',
    color: 'accent'
  },
  {
    id: '4',
    title: 'Track Orders',
    description: 'Monitor your delivery status in real-time',
    icon: 'TruckIcon',
    link: '/order-tracking',
    color: 'success'
  }];


  const subscriptionPlans = [
  {
    id: '1',
    name: 'Basic Plan',
    price: 1999,
    frequency: 'month',
    features: [
    '4 deliveries per month',
    'Free delivery on all orders',
    '5% discount on all products',
    'Priority customer support',
    'Flexible delivery scheduling'],

    savings: '₹300'
  },
  {
    id: '2',
    name: 'Premium Plan',
    price: 3499,
    frequency: 'month',
    features: [
    '8 deliveries per month',
    'Free delivery on all orders',
    '10% discount on all products',
    '24/7 priority support',
    'Same-day delivery option',
    'Exclusive premium products'],

    savings: '₹700',
    popular: true
  },
  {
    id: '3',
    name: 'Family Plan',
    price: 5999,
    frequency: 'month',
    features: [
    'Unlimited deliveries',
    'Free delivery on all orders',
    '15% discount on all products',
    'Dedicated account manager',
    'Custom meal planning',
    'Bulk order discounts'],

    savings: '₹1200'
  }];


  const trustSignals = [
  {
    id: '1',
    icon: 'ShieldCheckIcon',
    title: 'Quality Assured',
    description: 'FSSAI certified with 100% quality guarantee on every product'
  },
  {
    id: '2',
    icon: 'TruckIcon',
    title: 'Fast Delivery',
    description: 'Same-day delivery available for orders placed before 2 PM'
  },
  {
    id: '3',
    icon: 'CurrencyRupeeIcon',
    title: 'Best Prices',
    description: 'Direct sourcing ensures competitive pricing without middlemen'
  },
  {
    id: '4',
    icon: 'ClockIcon',
    title: 'Fresh Daily',
    description: 'New catch arrives every morning, ensuring maximum freshness'
  }];


  const handleSearch = (query: string) => {
    if (!isHydrated) return;
    router.push(`/product-listing?search=${encodeURIComponent(query)}`);
  };

  const handleVoiceSearch = () => {
    if (!isHydrated) return;
    console.log('Voice search activated');
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-96 bg-muted animate-pulse" />
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <HeroSearchSection onSearch={handleSearch} onVoiceSearch={handleVoiceSearch} />
      <TrustSignals signals={trustSignals} />
      <FeaturedCategories categories={categories} />
      <TodaysFreshCatch products={freshCatch} />
      <SpecialOffersCarousel offers={offers} />
      <QuickActions actions={quickActions} />
      <SubscriptionBanner plans={subscriptionPlans} />
    </div>);

};

export default HomeDashboardInteractive;